import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Inbox,
  Send,
  ArrowUpRight,
  ArrowDownLeft,
  Loader2,
} from 'lucide-react';
import {
  SearchInput,
  ScrollArea,
  Pagination,
  useAuth,
} from '@aaas/shared';
import { useNavigate } from 'react-router-dom';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { useEmailThreadListQuery } from '../../hooks/useEmailQueries';
import { format, isToday, isThisYear } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { EmailThread } from '../../types';
import { EmailsFilterSidebar, type EmailDirectionFilter } from './EmailsFilterSidebar';

type DirectionFilter = EmailDirectionFilter;

function formatSmartDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, 'HH:mm');
  if (isThisYear(date)) return format(date, 'dd MMM', { locale: vi });
  return format(date, 'dd/MM/yyyy');
}

export function EmailListPage() {
  const { t } = useTranslation('ats');
  const { currentWorkspace, idToken } = useAuth();
  const navigate = useNavigate();
  const { page, size, filters, setPage, setSize, setFilters } = useUrlPagination({
    filterKeys: ['search', 'direction'],
  });
  const searchValue = filters.search as string | undefined;
  const directionFilter = (filters.direction as DirectionFilter) || 'all';

  const sidebarItems: { key: DirectionFilter; label: string; icon: typeof Mail }[] = [
    { key: 'all', label: t('email.filterAll'), icon: Mail },
    { key: 'received', label: t('email.filterInbox'), icon: Inbox },
    { key: 'sent', label: t('email.filterSent'), icon: Send },
  ];

  const {
    data: threadResponse,
    isLoading,
    isFetching,
  } = useEmailThreadListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    search: searchValue,
    direction: directionFilter === 'all' ? undefined : directionFilter,
    page,
    size,
    enabled: !!currentWorkspace?.id && !!idToken,
  });

  const threads = threadResponse?.data?.items || [];
  const totalItems = threadResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleDirectionChange = useCallback(
    (dir: DirectionFilter) => {
      setFilters({ ...filters, direction: dir === 'all' ? undefined : dir });
    },
    [filters, setFilters]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setFilters({ ...filters, search: value || undefined });
    },
    [filters, setFilters]
  );

  return (
    <div className="flex h-[calc(100vh-64px)] bg-white rounded-lg border overflow-hidden">
      {/* Filter Sidebar - desktop only */}
      <EmailsFilterSidebar
        activeFilter={directionFilter}
        onFilterChange={handleDirectionChange}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="px-4 py-2.5 border-b flex items-center gap-3">
          <Mail className="h-5 w-5 text-orange-600 shrink-0" />
          <SearchInput
            initialValue={searchValue || ''}
            onSearch={handleSearch}
            placeholder={t('email.searchPlaceholder')}
            className="flex-1"
          />
          {isFetching && !isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400 shrink-0" />
          )}
        </div>

        {/* Mobile direction filter tabs (lg:hidden) */}
        <div className="lg:hidden px-4 py-2 border-b">
          <div className="flex rounded-md border overflow-hidden text-sm">
            {sidebarItems.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleDirectionChange(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors ${
                  directionFilter === key
                    ? 'bg-orange-50 text-orange-700 font-medium'
                    : 'text-gray-500 hover:bg-gray-50'
                } ${key !== 'all' ? 'border-l' : ''}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Thread rows */}
        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Mail className="h-10 w-10 mb-3" />
              <p className="text-sm">{t('email.noEmailsFound')}</p>
            </div>
          ) : (
            <div>
              {threads.map((thread) => (
                <ThreadRow
                  key={thread.id}
                  thread={thread}
                  onClick={() => navigate(`/ats/emails/${thread.id}`)}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-2 py-2">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={size}
              onPageSizeChange={setSize}
              totalItems={totalItems}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Thread Row (Gmail-style) ─── */

function ThreadRow({
  thread,
  onClick,
}: {
  thread: EmailThread;
  onClick: () => void;
}) {
  const { t } = useTranslation('ats');
  const isBold = thread.has_inbound;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center px-4 py-2.5 text-sm border-b border-gray-100 hover:bg-gray-50 hover:shadow-[inset_1px_0_0_#dadce0,inset_-1px_0_0_#dadce0,0_1px_2px_0_rgba(60,64,67,.3),0_1px_3px_1px_rgba(60,64,67,.15)] transition-all cursor-pointer group"
    >
      {/* Direction indicator */}
      <div className="w-6 shrink-0 flex justify-center">
        {thread.has_inbound ? (
          <ArrowDownLeft className="h-4 w-4 text-blue-500" />
        ) : (
          <ArrowUpRight className="h-4 w-4 text-orange-500" />
        )}
      </div>

      {/* Sender / candidate name */}
      <div
        className={`w-44 shrink-0 truncate pr-4 text-left ${
          isBold ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'
        }`}
      >
        {thread.candidate_name || t('email.unknown')}
        {thread.message_count && thread.message_count > 1 && (
          <span className="text-xs text-gray-400 font-normal ml-1">
            ({thread.message_count})
          </span>
        )}
      </div>

      {/* Subject + preview */}
      <div className="flex-1 min-w-0 flex items-center text-left">
        <span
          className={`truncate ${
            isBold ? 'font-semibold text-gray-900' : 'font-normal text-gray-800'
          }`}
        >
          {thread.subject}
        </span>
        {thread.last_message_preview && (
          <span className="text-gray-400 truncate ml-1 hidden sm:inline">
            — {thread.last_message_preview}
          </span>
        )}
      </div>

      {/* Job code badge */}
      {thread.job_code && (
        <span className="shrink-0 mx-2 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded hidden md:inline-flex">
          {thread.job_code}
        </span>
      )}

      {/* Date */}
      <div className="w-20 shrink-0 text-right text-xs text-gray-500">
        {formatSmartDate(thread.last_message_at)}
      </div>
    </button>
  );
}
